<script setup lang="ts">
interface Pool {
  name: string;
  type: "freshwater" | "seawater";
  image: string;
  image_alt?: string;
  description: string;
  menu_url?: string;
  menu_label?: string;
}

interface Tariff {
  label: string;
  covers: string;
  standard: string;
  guest: string;
  savings?: string;
  highlight?: boolean;
}

interface QuickFact {
  icon: "clock" | "calendar" | "age" | "ticket";
  label: string;
  value: string;
}

interface ShowcaseBlock {
  header: string;
  intro?: string;
  note?: string;
  compare_intro?: string;
  pools: Pool[];
  tariffs: Tariff[];
  quick_facts?: QuickFact[];
}

const props = defineProps<{
  block: ShowcaseBlock;
}>();

const { t } = useI18n();
const localePath = useLocalePath();

const freshwaterPools = computed(() =>
  props.block.pools.filter((p) => p.type === "freshwater"),
);
const seawaterPool = computed(() =>
  props.block.pools.find((p) => p.type === "seawater"),
);
</script>

<template>
  <section
    class="relative overflow-hidden bg-gradient-to-b from-white via-slate-50 to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-sky-200/25 blur-3xl dark:bg-teal-500/10"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-amber-200/30 blur-3xl dark:bg-amber-500/10"
    />

    <div data-reveal class="relative mx-auto w-full max-w-7xl px-4 py-8 md:py-12">
      <header class="mb-6 md:mb-8">
        <span
          class="font-body text-xs uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400"
        >
          {{ t("amenities.showcase.eyebrow") }}
        </span>
        <h2
          class="mt-3 max-w-5xl font-display text-3xl italic leading-tight text-slate-900 md:text-5xl dark:text-white"
        >
          {{ block.header }}
        </h2>
        <div class="mt-3 h-1 w-16 rounded-full bg-amber-400" />
        <p
          v-if="block.compare_intro"
          class="mt-3 max-w-3xl font-body text-base leading-relaxed text-slate-600 md:mt-4 md:text-lg dark:text-slate-300"
        >
          {{ block.compare_intro }}
        </p>
      </header>

      <div class="flex flex-col gap-5 md:gap-6">
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-5">
          <article
            v-for="pool in freshwaterPools"
            :key="pool.name"
            class="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-slate-700 dark:bg-slate-800/60"
          >
            <div class="aspect-[4/3] overflow-hidden">
              <NuxtImg
                :src="pool.image"
                :alt="pool.image_alt ?? pool.name"
                loading="lazy"
                width="600"
                height="450"
                sizes="sm:50vw md:33vw"
                class="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div class="p-5 md:p-6">
              <h3
                class="font-display text-xl italic text-slate-900 md:text-2xl dark:text-white"
              >
                {{ pool.name }}
              </h3>
              <p
                class="mt-2 font-body text-sm leading-relaxed text-slate-600 dark:text-slate-400"
              >
                {{ pool.description }}
              </p>
              <a
                v-if="pool.menu_url && pool.menu_label"
                :href="pool.menu_url"
                target="_blank"
                rel="noopener noreferrer"
                class="mt-3 inline-flex items-center gap-1.5 font-body text-sm font-semibold text-amber-700 transition-colors hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-200"
                :aria-label="`${pool.menu_label} — ${pool.name}`"
              >
                {{ pool.menu_label }}
                <Icon
                  name="lucide:external-link"
                  class="size-3.5 shrink-0"
                  aria-hidden="true"
                />
              </a>
            </div>
          </article>
        </div>

        <article
          v-if="seawaterPool"
          class="group relative overflow-hidden rounded-2xl border border-teal-200/70 bg-gradient-to-br from-teal-50 via-white to-white shadow-sm transition-all duration-300 hover:shadow-xl dark:border-teal-900/50 dark:from-teal-950/40 dark:via-slate-800/60 dark:to-slate-800/60"
        >
          <div class="grid md:grid-cols-5">
            <div
              class="aspect-[16/10] overflow-hidden md:col-span-2 md:aspect-auto"
            >
              <NuxtImg
                :src="seawaterPool.image"
                :alt="seawaterPool.image_alt ?? seawaterPool.name"
                loading="lazy"
                width="800"
                height="500"
                sizes="md:40vw"
                class="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div
              class="relative flex flex-col justify-center p-6 md:col-span-3 md:p-10"
            >
              <div
                aria-hidden="true"
                class="absolute left-0 top-6 hidden h-12 w-0.5 rounded-r-full bg-teal-400 md:block dark:bg-teal-500"
              />
              <span
                class="inline-flex items-center self-start rounded-full bg-teal-100 px-2.5 py-1 font-body text-[10px] font-semibold uppercase tracking-[0.2em] text-teal-800 dark:bg-teal-900/50 dark:text-teal-200"
              >
                {{ t("amenities.showcase.type_seawater") }}
              </span>
              <h3
                class="mt-4 font-display text-2xl italic text-slate-900 md:text-3xl dark:text-white"
              >
                {{ seawaterPool.name }}
              </h3>
              <p
                class="mt-3 max-w-xl font-body text-base leading-relaxed text-slate-600 dark:text-slate-300"
              >
                {{ seawaterPool.description }}
              </p>
            </div>
          </div>
        </article>
      </div>

      <div
        v-if="block.tariffs?.length"
        class="mt-5 md:mt-6"
      >
        <div class="grid gap-5 md:grid-cols-2 md:gap-6">
          <article
            v-for="tariff in block.tariffs"
            :key="tariff.label"
            class="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md md:p-7 dark:border-slate-700 dark:bg-slate-800/50"
          >
            <div class="flex flex-col gap-1.5">
              <span
                class="font-body text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400"
              >
                {{ tariff.covers }}
              </span>
              <h4
                class="font-display text-lg italic text-slate-900 md:text-xl dark:text-white"
              >
                {{ tariff.label }}
              </h4>
            </div>

            <div
              class="mt-6 grid grid-cols-[1fr_auto_1fr] items-end gap-3 md:gap-5"
            >
              <div>
                <span
                  class="block font-body text-[10px] uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500"
                >
                  {{ t("amenities.compare.walkin_label") }}
                </span>
                <span
                  class="mt-1.5 block font-display text-2xl tabular-nums text-slate-400 line-through decoration-2 decoration-slate-300 md:text-3xl dark:text-slate-500 dark:decoration-slate-600"
                >
                  {{ tariff.standard }}
                </span>
              </div>

              <span
                aria-hidden="true"
                class="mb-2 flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 md:h-9 md:w-9"
              >
                <svg
                  class="h-4 w-4 md:h-5 md:w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </span>

              <div class="text-right">
                <span
                  class="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 font-body text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                >
                  {{ t("amenities.compare.guest_label") }}
                </span>
                <span
                  class="mt-1.5 block font-display text-3xl font-semibold tabular-nums text-slate-900 md:text-4xl dark:text-white"
                >
                  {{ tariff.guest }}
                </span>
              </div>
            </div>

            <div
              v-if="tariff.savings"
              class="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-dashed border-slate-200 pt-4 dark:border-slate-700"
            >
              <span
                class="inline-flex items-center rounded-md bg-emerald-50 px-2 py-0.5 font-body text-sm font-semibold text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400"
              >
                {{ t("amenities.compare.savings_prefix") }} {{ tariff.savings }}
              </span>
              <span
                class="font-body text-xs text-slate-500 dark:text-slate-400"
              >
                {{ t("amenities.compare.peak_subtitle") }}
              </span>
            </div>
          </article>
        </div>

        <div
          v-if="block.quick_facts?.length"
          class="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4"
        >
          <div
            v-for="fact in block.quick_facts"
            :key="fact.icon + fact.label"
            class="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white/90 px-4 py-4 shadow-sm backdrop-blur-sm transition-shadow hover:shadow-md md:px-5 dark:border-slate-700 dark:bg-slate-800/70"
          >
            <span
              class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
            >
              <svg
                v-if="fact.icon === 'clock'"
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
              <svg
                v-else-if="fact.icon === 'calendar'"
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <rect x="3" y="5" width="18" height="16" rx="2" />
                <path d="M8 3v4M16 3v4M3 10h18" />
              </svg>
              <svg
                v-else-if="fact.icon === 'age'"
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="6" r="2.5" />
                <path d="M7 21v-5l-2-4 2-3h10l2 3-2 4v5" />
              </svg>
              <svg
                v-else-if="fact.icon === 'ticket'"
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path
                  d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4Z"
                />
                <path d="M13 6v12" stroke-dasharray="2 2" />
              </svg>
            </span>
            <div class="min-w-0 flex-1 pt-0.5">
              <span
                class="block font-body text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400"
              >
                {{ fact.label }}
              </span>
              <span
                class="block font-body text-sm font-semibold text-slate-900 dark:text-white"
              >
                {{ fact.value }}
              </span>
            </div>
          </div>
        </div>

        <div
          class="mt-5 flex flex-col items-start gap-3 border-t border-dashed border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between sm:gap-5 dark:border-slate-700"
        >
          <p
            v-if="block.note"
            class="inline-flex items-start gap-2 font-body text-sm text-slate-600 dark:text-slate-300"
          >
            <svg
              class="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4M12 8h.01" />
            </svg>
            <span>{{ block.note }}</span>
          </p>
          <NuxtLink
            :to="localePath('/search')"
            class="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-red-500 px-5 py-2 font-body text-sm font-semibold uppercase tracking-[0.15em] text-white shadow-md shadow-red-500/25 transition-all duration-300 hover:bg-red-600 hover:shadow-red-500/40"
          >
            {{ t('apartmentRow.cta.book') }}
            <svg
              class="h-3.5 w-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </NuxtLink>
        </div>
      </div>
    </div>
  </section>
</template>
